import React from 'react';
import styled, {css} from 'styled-components';
import _ from 'lodash';

import {HeaderTitle} from '../../../shared-components/custom-features/app-element/Title';
import {ScrollBar} from '../../../shared-components/mixin/scroll/scrollBar';
import {
    formControlColors,
    inContainerFormControlColors,
} from '../../../shared-components/variables/componentColors/formControlVariableColors';

import {Divider} from '../../custom-feature/divider/Divider';
import {EmptyState} from '../../custom-feature/empty-state/EmptyState';
import {useMainContentHeight} from '../../../page-structure/useMainContentHeight';
import {AsideActionHeader} from './AsideActionHeader';
import {AsideNav} from './AsideNavigations';
import {AsideRow, AsideRowWrapper} from './AsideRow';
import {ModalCloseBtn} from '../../elements/button/btn-type/ModalCloseBtn';
import {Card} from '../../elements/card-v2/Card';
import {FormGroup} from '../../form-v2/form-containers/FormGroup';
import {colorTheme} from '../../mixin/colorTheme';
import {AsideSizeSetup} from '../page-features/PageSizeSetup';

/**
 * @description
 * The <Aside> component serves as a styled element for a fixed sidebar. Once positioned within your grid layout, you can integrate additional components to enrich the user interface design. Here are some examples of enhancements you can make:
 *
 * **Aside Header**
 *
 * You can use the <Aside.Header/> component to set a header title for the Aside.
 *
 * **Aside Action Header**
 *
 * You can use the `<AsideActionHeader/>` to create a section in an aside that contains a title on the
 * left side (see also the `title` prop, and a button on the right side (see also the `actionButton` prop)
 *
 *  **Aside Body**
 *
 * You can utilize the <Aside.Body/> component to designate the main content section within the Aside. This will also enable a scroll effect for the content within the Aside.Body.
 *
 *  **Aside Section**
 *
 * The <Aside.Section> component serves as the body of the <Aside> component,
 * residing within the <Aside.Body>. It is used to enclose content within the sidebar,
 * providing padding and separating different sections. You can utilize multiple <Aside.Section> components within <Aside.Body>
 * to organize and structure your content effectively.
 *
 * **Aside Nav**
 *
 *  The `<Aside.Nav> `component is used as a navigation feature within the <Aside> component to filter the page you are using and display other content with NavLink from React.
 *
 * **Aside Divider**
 *
 * The `<Divider>` component is utilized to insert a border or line between content sections within the sidebar. It can be used without affecting the padding.
 * You can place `<Divider>` between `<Aside.Section>` components or within the content of an `<Aside.Section>` to visually separate different sections.
 *
 * **Aside CloseBtn**
 *
 * This is a button component designed to display a close icon in gray color. When hovered over, it changes to red. This component is intended to be used within an `Aside` component as the close button for `Aside` modal components.
 * To use this, you should apply the component `<Aside.CloseBtn onClick={onClose} />` inside the component `<Aside.Header>`. If you are including a title in this header, make sure to place the button on the right side. You can view the example below to see how to do this.
 *
 * @param {object} param0
 *
 * @param {React.ReactNode} param0.children
 *
 * @param {"sm" | "lg" | "xl"} [param0.size]
 * The size prop adjusts the width of the Aside component.
 * Available options:
 * - 'sm': for a thinner width
 * - 'lg': for a wider width
 *
 * @example
 * ```js
 * // === Aside.Section === //
 *  <Aside>
 *      <Aside.Body>
 *          <Aside.Section>
 *              Put your content here
 *          </Aside.Section>
 *          <Aside.Section>
 *              Put your content here
 *          </Aside.Section>
 *      </Aside.Body>
 *  </Aside>
 *
 * // === Aside Divider === //
 *  <Aside>
 *      <Aside.Section>
 *          <Card />
 *          <Divider>
 *          <Card />
 *      </Aside.Section>
 *      <Divider>
 *      <Aside.Section>
 *          Put your content here
 *      </Aside.Section>
 *  </Aside>
 * ```
 */
const AsideComponent = ({children, size}) => {
    const {targetRef} = useMainContentHeight();

    return (
        <AsideWrapper ref={targetRef} className={size}>
            <Wrapper>{children}</Wrapper>
        </AsideWrapper>
    );
};

export const AsideEmptyState = ({children}) => {
    return (
        <>
            <AsideSection>
                <EmptyState.Txt>{children}</EmptyState.Txt>
            </AsideSection>
            <Divider />
        </>
    );
};

/**
 * @description
 * **Aside Header**
 * You can use the <Aside.Header/> component to set a header title for the Aside.
 * When using this, make sure to follow the example below:
 *
 * @example
 * ```js
 * // === Aside.Section === //
 *  <Aside>
 *      <Aside.Header>
 *          Header text title
 *      </Aside.Header>
 *      <Aside.Body>
 *          <Aside.Section>
 *              Put your content here
 *          </Aside.Section>
 *          <Aside.Section>
 *              Put your content here
 *          </Aside.Section>
 *      </Aside.Body>
 *  </Aside>
 * ```
 */
export const AsideHeader = styled(HeaderTitle)`
    padding: var(--aside-padding) var(--aside-padding) 0px;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

/**
 * @description
 * **Aside Footer**
 * You can use the <Aside.Footer/> component to set footer content below the Aside. Add content such as buttons.
 * When using this, make sure to follow the example below:
 *
 * @example
 * ```js
 * // === Aside.Section === //
 * With this exact structure, you'll have the footer fixed at the bottom of the Aside.
 *  <Aside>
 *      <Aside.Header>
 *          Header text title
 *      </Aside.Header>
 *      <Aside.Body>
 *          <Aside.Section>
 *              Put your content here
 *          </Aside.Section>
 *          <Aside.Section>
 *              Put your content here
 *          </Aside.Section>
 *      </Aside.Body>
 *      <Aside.Footer>
 *          <Btn>Cancel</Btn>
 *      </Aside.Footer>
 *  </Aside>
 * ```
 */
export const AsideSection = styled.div`
    padding: var(--aside-padding);
    word-break: break-word;

    ${Divider} {
        margin: var(--aside-padding) calc(0px - var(--aside-padding));
        width: calc(100% + (var(--aside-padding) * 2));
    }
`;

export const AsideFooter = styled.div`
    width: var(--aside-width);
    display: flex;
    align-items: center;
    gap: var(--space-xsm);
    padding: var(--aside-padding);
    background: var(--container-background-color-600);
`;

export const AsideBodyScroll = styled(ScrollBar)`
    @-moz-document url-prefix() {
        overflow-y: auto;
        scrollbar-color: var(--scroll-bar-background) var(--scroll-bar-thumb);
    }
`;

export const AsideGridSetup = () => css`
    overflow-y: hidden;
    overflow-x: hidden;
    display: grid;

    ${AsideHeader} {
        grid-area: asideHeader;
    }

    ${AsideBodyScroll} {
        overflow-y: auto;
        overflow-x: hidden;
    }

    ${AsideRowWrapper},
    ${AsideBodyScroll} {
        grid-area: asideBody;
    }

    ${AsideFooter} {
        grid-area: asideFooter;
    }
`;

const defaultAsidePattern = `
    > ${AsideHeader.toString()}:nth-child(1)
    + ${AsideBodyScroll.toString()}:nth-child(2)
    + ${AsideFooter.toString()}:nth-child(3)
`;

const nonHeaderAsidePattern = `
     ${AsideBodyScroll.toString()}:nth-child(1)
    + ${AsideFooter.toString()}:nth-child(2)
`;
const Wrapper = styled.div`
    position: relative;
    height: 100%;
`;

const AsideWrapper = styled(ScrollBar)`
    ${AsideSizeSetup}
    --aside-padding: var(--grid-padding);
    width: var(--aside-width);
    z-index: 200;

    ${inContainerFormControlColors};
    ${colorTheme({
        '--fixed-sidebar-scroll-thumb      ': {dark: 'var(--black-14)  ', light: 'var(--gray-02)'},
    })};
    --scroll-bar-background: var(--container-background-color-600) !important;
    --scroll-bar-thumb: var(--fixed-sidebar-scroll-thumb) !important;
    --scroll-bar-thumb-hover: var(--container-background-color-600) !important;

    grid-area: aside;
    background: var(--container-background-color-600);
    box-shadow: var(--container-shadow-color-500);
    overflow-y: auto;
    overflow-x: hidden;

    &:has(${defaultAsidePattern}),
    &:has(> form:nth-child(1)) > form:has(${defaultAsidePattern}) {
        ${AsideGridSetup};
        grid-template-areas:
            'asideHeader'
            'asideBody'
            'asideFooter';

        grid-template-rows: auto 1fr auto;
    }

    &:has(${nonHeaderAsidePattern}),
    &:has(> form:nth-child(1)) > form:has(${nonHeaderAsidePattern}) {
        ${AsideGridSetup};
        grid-template-areas:
            'asideHeader'
            'asideBody'
            'asideFooter';
        grid-template-rows: 1fr auto;
    }

    &:has(> form:nth-child(1)) {
        display: grid;
        grid-template-areas: 'asideForm';
        grid-template-rows: 1fr;
    }

    // Variable modification

    ${FormGroup} {
        --form-box-background-color: var(--container-background-color-200);
        --form-box-border-color: var(--container-border-color-200);
        --form-labeled-affix-background-color: var(--container-background-color-500);
        --form-labeled-affix-border-color: var(--container-border-color-200);
    }

    ${Card.Wrapper} {
        background: var(--container-background-color-200);
        box-shadow: var(--container-shadow-color-200);
        border: 1px solid var(--container-border-color-600);

        ${formControlColors};

        ${FormGroup} {
            --form-box-background-color: var(--container-background-color-100);
            --form-box-border-color: var(--container-border-color-100);
            --form-labeled-affix-background-color: var(--container-background-color-300);
            --form-labeled-affix-border-color: var(--container-border-color-100);
        }

        // v1 temp modifications
        ${colorTheme({
            '--card-sidebar-input-background-color': {dark: 'var(--black-04)', light: 'var(--white-00)'},
            '--card-sidebar-input-border-color': {dark: 'var(--black-02)', light: 'var(--gray-02)'},
        })};
        --form-control-background-color: var(--card-sidebar-input-background-color);
        --form-control-border-color: var(--card-sidebar-input-border-color);
        --form-word-fix-border-color: var(--card-sidebar-input-border-color);
    }
`;

/**
 * @description
 *
 * This is a button component designed to display a close icon in gray color. When hovered over, it changes to red. This component is intended to be used within an `Aside` component as the close button for `Aside` modal components.
 * To use this, you should apply the component <Aside.CloseBtn onClick={onClose} /> inside the component <Aside.Header>. If you are including a title in this header, make sure to place the button on the right side. You can view the example below to see how to do this."
 *
 * @param {object} param0
 *
 * @param {Function} [param0.onClick]
 * Callback function triggered when the button is clicked.
 *
 * @example
 *  ```jsx
 *
 *  <Aside.Header>
 *      Header title
 *      <Aside.CloseBtn onClick={onClose} />
 *  </Aside.Header>
 * ```
 */

export const Aside = _.assign(AsideComponent, {
    CloseBtn: ModalCloseBtn,
    Wrapper: AsideWrapper,
    Header: AsideHeader,

    Body: AsideBodyScroll,
    Row: AsideRow,

    RowWrapper: AsideRowWrapper,
    Section: AsideSection,
    Footer: AsideFooter,

    ActionHeader: AsideActionHeader,
    Nav: AsideNav,

    EmptyState: AsideEmptyState,
});

export {AsideWrapper};
